/**
 * Shared payload construction for the Media Matchers Search and Post calls.
 * All three Search calls send the same params; only the endpoint differs.
 */
import { readLeadIdToken, readTrustedFormCert } from '../../hooks/useLeadTracking'
import { getLandingSearch } from '../../utils/preserveParams'

/** This page's own publisher id, used when the URL carries no override. */
const DEFAULT_PUB_GROUP_ID = 'PCGP4446938514'

const SESSION_KEY = 'mm_session_id'
const LANDING_URL_KEY = 'mm_landing_url'

export function uuid(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** One session id, generated once and reused on every call. */
export function getSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY)
    if (existing) return existing
    const next = uuid()
    sessionStorage.setItem(SESSION_KEY, next)
    return next
  } catch {
    return uuid()
  }
}

function readCookie(name: string): string {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)'),
  )
  return match ? decodeURIComponent(match[1]) : ''
}

export interface MetaCookies {
  fbc: string
  fbp: string
}

export function getMetaCookies(): MetaCookies {
  return { fbc: readCookie('_fbc'), fbp: readCookie('_fbp') }
}

/**
 * Publisher params. A group id on the URL wins outright; otherwise the
 * mPubID/mPubCampaignID pair is honoured only when BOTH are present; otherwise
 * this page's default group id is sent. A group id is never mixed with the pair.
 */
export function applyPublisherParams(
  target: URLSearchParams,
  search: string = getLandingSearch(),
): void {
  const url = new URLSearchParams(search)
  const groupId = url.get('mPubGroupID')?.trim()
  if (groupId) {
    target.set('mPubGroupID', groupId)
    return
  }
  const pubId = url.get('mPubID')?.trim()
  const campaignId = url.get('mPubCampaignID')?.trim()
  if (pubId && campaignId) {
    target.set('mPubID', pubId)
    target.set('mPubCampaignID', campaignId)
    return
  }
  target.set('mPubGroupID', DEFAULT_PUB_GROUP_ID)
}

/**
 * sub3 doubles as the Meta match key: when either Meta cookie exists it becomes
 * `fbc_fbp` (the server splits on the underscore); otherwise the URL's own sub3
 * passes through untouched.
 */
export function resolveSubIds(search: string = getLandingSearch()): {
  sub1: string
  sub2: string
  sub3: string
} {
  const url = new URLSearchParams(search)
  const { fbc, fbp } = getMetaCookies()
  const sub3 = fbc || fbp ? `${fbc}_${fbp}` : url.get('sub3')?.trim() || ''
  return {
    sub1: url.get('sub1')?.trim() || '',
    sub2: url.get('sub2')?.trim() || '',
    sub3,
  }
}

// --- Value mappings -------------------------------------------------------

/**
 * The partner spec only defines 1, 2, 5, 7, and 9, which is why several form
 * options collapse onto the same code.
 */
export function mapHighestEducation(label: string): string {
  const v = (label || '').toLowerCase()
  // Order matters: "some high school" and "no high school diploma" must be
  // tested before the plain "high school" match.
  if (v.includes('some high school') || v.includes('no high school')) return '2'
  if (v.includes('high school') || v.includes('ged')) return '1'
  if (v.includes('some college')) return '5'
  if (v.includes('associate')) return '5'
  if (v.includes('bachelor')) return '7'
  if (v.includes('master')) return '7'
  if (v.includes('doctorate') || v.includes('professional')) return '7'
  return '9'
}

/** Inbound codes map back one-to-one onto four of the form's options. */
const HIGHEST_EDUCATION_INBOUND: Record<string, string> = {
  '1': 'High School Diploma / GED',
  '2': 'Some High School',
  '5': 'Some College',
  '7': "Bachelor's Degree",
}

export function decodeHighestEducation(value: string): string {
  const v = (value || '').trim()
  if (!v) return ''
  if (HIGHEST_EDUCATION_INBOUND[v]) return HIGHEST_EDUCATION_INBOUND[v]
  // Slug form, e.g. "some-college".
  const slug = v.toLowerCase().replace(/-/g, ' ')
  const match = Object.values(HIGHEST_EDUCATION_INBOUND).find(
    (label) => label.toLowerCase().replace(/[^a-z ]/g, '').trim() === slug,
  )
  return match || ''
}

/**
 * Note the deliberate asymmetry with the inbound codes: "More than a year" is
 * SENT as 4 but RECEIVED as 5, per the partner spec.
 */
export function mapStartTimeframe(label: string): string {
  const v = (label || '').toLowerCase()
  if (v.includes('immediate')) return '1'
  if (v.includes('1–3') || v.includes('1-3')) return '2'
  if (v.includes('3–6') || v.includes('3-6')) return '3'
  if (v.includes('6–12') || v.includes('6-12')) return '4'
  if (v.includes('more than a year')) return '4'
  return '6'
}

const START_TIMEFRAME_INBOUND: Record<string, string> = {
  '1': 'Immediately',
  '2': '1–3 months',
  '3': '3–6 months',
  '4': '6–12 months',
  '5': 'More than a year',
  '6': 'Undecided',
}

export function decodeStartTimeframe(value: string): string {
  const v = (value || '').trim()
  if (!v) return ''
  return START_TIMEFRAME_INBOUND[v] || ''
}

/** "Unknown" has no code in the spec and is sent empty. */
export function mapMilitaryAffiliation(label: string): string {
  const v = (label || '').toLowerCase()
  if (!v || v.includes('unknown')) return ''
  if (v === '1' || v.includes('none')) return '1'
  if (v === '2' || v.includes('active')) return '2'
  if (v === '3' || v.includes('veteran')) return '3'
  if (v === '4' || v.includes('guard') || v.includes('reserve')) return '4'
  if (v === '5' || v.includes('spouse')) return '5'
  if (v === '6' || v.includes('dependent')) return '6'
  return ''
}

/**
 * Sent as a literal 4-digit year; "Before 1965" sends 1965. Anything outside
 * the last 60 years is rejected as invalid.
 */
export function normalizeGradYear(label: string): string {
  const v = (label || '').trim()
  if (!v) return ''
  if (/before/i.test(v)) return '1965'
  const match = v.match(/\d{4}/)
  if (!match) return ''
  const year = Number(match[0])
  const currentYear = new Date().getFullYear()
  if (year === 1965) return '1965'
  if (year < currentYear - 60 || year > currentYear + 10) return ''
  return match[0]
}

const STATE_ABBR: Record<string, string> = {
  alabama: 'AL', alaska: 'AK', arizona: 'AZ', arkansas: 'AR',
  california: 'CA', colorado: 'CO', connecticut: 'CT', delaware: 'DE',
  'district of columbia': 'DC', florida: 'FL', georgia: 'GA', hawaii: 'HI',
  idaho: 'ID', illinois: 'IL', indiana: 'IN', iowa: 'IA', kansas: 'KS',
  kentucky: 'KY', louisiana: 'LA', maine: 'ME', maryland: 'MD',
  massachusetts: 'MA', michigan: 'MI', minnesota: 'MN', mississippi: 'MS',
  missouri: 'MO', montana: 'MT', nebraska: 'NE', nevada: 'NV',
  'new hampshire': 'NH', 'new jersey': 'NJ', 'new mexico': 'NM',
  'new york': 'NY', 'north carolina': 'NC', 'north dakota': 'ND', ohio: 'OH',
  oklahoma: 'OK', oregon: 'OR', pennsylvania: 'PA', 'puerto rico': 'PR',
  'rhode island': 'RI', 'south carolina': 'SC', 'south dakota': 'SD',
  tennessee: 'TN', texas: 'TX', utah: 'UT', vermont: 'VT', virginia: 'VA',
  washington: 'WA', 'west virginia': 'WV', wisconsin: 'WI', wyoming: 'WY',
}

/** Full state names are converted; a 2-letter value passes through upper-cased. */
export function normalizeState(value: string): string {
  const v = (value || '').trim()
  if (!v) return ''
  if (v.length === 2) return v.toUpperCase()
  return STATE_ABBR[v.toLowerCase()] || ''
}

/** Digits only, first 5. */
export function normalizeZip(value: string): string {
  return (value || '').replace(/\D/g, '').slice(0, 5)
}

/** The area is always derivable from the program id: floor(mProgram / 100). */
export function areaFromProgram(mProgram: string): string {
  const n = Number(mProgram)
  if (!Number.isFinite(n) || n < 100) return ''
  return String(Math.floor(n / 100))
}

// --- Compliance field readers --------------------------------------------

function readHiddenInput(name: string): string {
  const el = document.querySelector(
    `input[name="${name}"]`,
  ) as HTMLInputElement | null
  return el?.value?.trim() || ''
}

export function getLeadIdToken(): string {
  return readLeadIdToken() || readHiddenInput('leadid_token') || uuid()
}

/**
 * Read through the full scan rather than just the hidden input: TrustedForm
 * exposes the cert on `window` as well, and the input element can be replaced
 * by a re-render after the script wrote to it.
 */
export function getTrustedFormUrl(): string {
  return readTrustedFormCert() || readHiddenInput('xxTrustedFormCertUrl')
}

/** The page the visitor landed on, without the query string. */
/** The page the visitor landed on, held for the whole session. */
export function getLandingUrl(): string {
  try {
    const stored = sessionStorage.getItem(LANDING_URL_KEY)
    if (stored) return stored
    const current = `${window.location.origin}${window.location.pathname}`
    sessionStorage.setItem(LANDING_URL_KEY, current)
    return current
  } catch {
    try {
      return `${window.location.origin}${window.location.pathname}`
    } catch {
      return ''
    }
  }
}

/**
 * Params are omitted from the query entirely when they have no value — an empty
 * `militaryAffiliation=` can fail a partner match that an absent key passes.
 */
export function setIf(
  target: URLSearchParams,
  key: string,
  value: string | undefined | null,
): void {
  const v = (value ?? '').toString().trim()
  if (v) target.set(key, v)
}

// --- Shared search payload ----------------------------------------------

export interface SearchProfile {
  degreeInterest: string
  areaStudy: string
  mProgram: string
  currentEducation: string
  gradYear: string
  startTimeframe: string
  militaryAffiliation: string
  zipCode: string
  address: string
  city: string
  /** Two-letter USPS abbreviation. */
  state: string
}

export interface ContactProfile {
  firstName: string
  lastName: string
  email: string
  /** Digits only. */
  phone: string
}

/**
 * Builds the payload every Search call shares. The TrustedForm cert is sent on
 * every call once it exists — a cert that arrives late is simply absent from
 * the earlier calls rather than sent empty.
 */
export function buildSearchParams(profile: SearchProfile): URLSearchParams {
  const params = new URLSearchParams()
  const subs = resolveSubIds()

  setIf(params, 'degreeInterest', profile.degreeInterest)
  setIf(
    params,
    'areaStudy',
    profile.areaStudy || areaFromProgram(profile.mProgram),
  )
  setIf(params, 'mProgram', profile.mProgram)
  setIf(params, 'highestEducation', mapHighestEducation(profile.currentEducation))
  setIf(params, 'gradYear', normalizeGradYear(profile.gradYear))
  // Always online.
  params.set('campusType', '1')
  setIf(
    params,
    'militaryAffiliation',
    mapMilitaryAffiliation(profile.militaryAffiliation),
  )
  setIf(params, 'startTimeframe', mapStartTimeframe(profile.startTimeframe))
  setIf(params, 'sub1', subs.sub1)
  setIf(params, 'sub2', subs.sub2)
  setIf(params, 'sub3', subs.sub3)
  setIf(params, 'zipCode', normalizeZip(profile.zipCode))
  setIf(params, 'address', profile.address)
  setIf(params, 'city', profile.city)
  setIf(params, 'state', normalizeState(profile.state))
  setIf(params, 'webLandingURL', getLandingUrl())
  setIf(params, 'leadIdToken', getLeadIdToken())
  const cert = getTrustedFormUrl()
  if (!cert) {
    console.warn(
      '[MM] No TrustedForm cert available yet — trustedFormUrl omitted from this call.',
    )
  }
  setIf(params, 'trustedFormUrl', cert)
  params.set('mSessionID', getSessionId())
  applyPublisherParams(params)
  return params
}
